/* eslint-disable import/no-extraneous-dependencies */
import React, { useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Grid,
  Typography,
  Accordion,
  Slider,
  AccordionSummary,
  TextField,
  AccordionDetails,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import axios from '../../utils/axios.utils';
import DataTable from '../../dataTable/DataTable/DataTable';
import { IDataTableColumn } from '../../dataTable/DataTable/model';
import SalesEstimateStepper from '../components/SalesEstimateStepper';
import SalesEstimateLoading from '../components/SalesEstimateLoading';
import { PDFDownloadLink } from '@react-pdf/renderer';
import EstimatePDF from '../../estimateReports/pages/EstimatePDF';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend } from 'chart.js';
import { Bar } from 'react-chartjs-2';
import InformationField from '../../form/components/InformationField';
import { SalesEstimate } from '../model';
import EstimateInfoHeader from '../../estimateCustomers/components/EstimateInfoHeader';
import ExpandedAssetCost from '../components/ExpandedAssetCost';
import ExpandedCostBreakdown from '../components/ExpandedCostBreakdown';
import { EstimateDetailsContext } from '../../estimateDetails/contexts/EstimateDetailsContext';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const DecodingData = {
  id: 1,
  name: 'NextLink Labs',
};

const useStyles = makeStyles(() => ({
  root: {
    display: 'flex',
    minHeight: '100vh',
    width: '100%',
    padding: 0,
  },
  box: {
    width: '100%',
  },
  content: {
    marginLeft: '0px',
    padding: '80px 30px',
  },
}));

const assetColumns: IDataTableColumn<any>[] = [
  {
    name: 'Asset Name',
    selector: 'name',
    sortable: true,
    cell: (row) => <p>{row.asset_number}</p>,
  },
  {
    name: 'Existing Model Number',
    selector: 'name',
    sortable: true,
    cell: (row) => <p>{row.model_number}</p>,
  },
  {
    name: 'Replacement Model Number',
    selector: 'name',
    sortable: true,
    cell: (row) => <p>{row.replacement_equipment_model}</p>,
  },
  {
    name: 'Size',
    selector: 'name',
    sortable: true,
    cell: (row) => <p>{row.size}</p>,
  },
  {
    name: 'Age',
    selector: 'vintage',
    sortable: true,
    cell: (row) => <p>{row.vintage}</p>,
  },
];

const costSummaryColumns: IDataTableColumn<any>[] = [
  {
    name: 'Cost Category',
    selector: 'category',
    sortable: true,
  },
  {
    name: 'Total Cost',
    selector: 'total_cost',
    sortable: true,
  },
  {
    name: 'GP%',
    selector: 'name',
    sortable: true,
    cell: (row) => <p>35%</p>,
  },
  {
    name: 'Sell Price',
    selector: 'sell_price',
    sortable: true,
  },
];

const keyDetailColumns: IDataTableColumn<any>[] = [
  {
    name: 'Folder Clean',
    selector: 'name',
    sortable: false,
    cell: (row) => <p>Yes</p>,
  },
  {
    name: 'Tax Exmpt',
    selector: 'name',
    sortable: false,
    cell: (row) => <p>No</p>,
  },
  {
    name: 'Night Work',
    selector: 'name',
    sortable: false,
    cell: (row) => <p>No</p>,
  },
  {
    name: 'Wknd Work',
    selector: 'name',
    sortable: false,
    cell: (row) => <p>No</p>,
  },
  {
    name: 'POC',
    selector: 'vintage',
    sortable: false,
    cell: (row) => <p>david</p>,
  },
  {
    name: 'Urgency',
    selector: 'vintage',
    sortable: false,
    cell: (row) => <p>ASAP</p>,
  },
  {
    name: 'Permit REQ',
    selector: 'vintage',
    sortable: false,
    cell: (row) => <p>No</p>,
  },
  {
    name: 'Turnover Mtg',
    selector: 'vintage',
    sortable: false,
    cell: (row) => <p>Phone</p>,
  },
  {
    name: 'Closeout Type',
    selector: 'vintage',
    sortable: false,
    cell: (row) => <p>None</p>,
  },
  {
    name: 'Startup Type',
    selector: 'vintage',
    sortable: false,
    cell: (row) => <p>Projects</p>,
  },
  {
    name: 'Lead Tech',
    selector: 'vintage',
    sortable: false,
    cell: (row) => <p>ANY</p>,
  },
];

const changeLogColumns: IDataTableColumn<any>[] = [
  {
    name: 'Field',
    selector: 'name',
    sortable: true,
    cell: (row) => <p>{row.asset_number}</p>,
  },
  {
    name: 'Previous Value',
    selector: 'name',
    sortable: true,
    cell: (row) => <p>{row.model_number}</p>,
  },
  {
    name: 'Updated Value',
    selector: 'name',
    sortable: true,
    cell: (row) => <p>{row.replacement_equipment_model}</p>,
  },
  {
    name: 'Performed By',
    selector: 'name',
    sortable: true,
    cell: (row) => <p>{row.size}</p>,
  },
  {
    name: 'Timestamp',
    selector: 'vintage',
    sortable: true,
    cell: (row) => <p>{row.vintage}</p>,
  },
];

const rebateDetails: IDataTableColumn<any>[] = [
  {
    name: 'Rebate Name',
    selector: 'name',
    sortable: true,
    // cell: (row) => <p>First Energy HVAC</p>,
  },
  {
    name: 'Rate',
    selector: 'rate',
    sortable: true,
    // cell: (row) => <p>$0.70</p>,
  },
  {
    name: 'Energy Savings',
    selector: 'energy_savings',
    sortable: true,
    // cell: (row) => <p>2,000</p>,
  },
  {
    name: 'Estimated Amount',
    selector: 'estimated_amount',
    sortable: true,
    // cell: (row) => <p>$1,400</p>,
  },
  {
    name: 'Actions',
    selector: 'code',
    sortable: true,
    grow: 1.5,
    cell: (row) => (
      <Button variant="contained" color="primary" onClick={() => null}>
        Submit For Review
      </Button>
    ),
  },
];

const formatNumber = (number) => (number != null ? parseFloat(number).toFixed(2) : '');

const DecodingDetail: React.FunctionComponent = () => {
  const classes = useStyles();
  const history = useHistory();

  const { estimateId } = useParams<{ estimateId: string }>();

  const [decoding, setDecoding] = React.useState<any>({ results: [] });

  const [grossProfitPercentage, setGrossProfitPercentage] = React.useState(30);
  const [salesEstimateFetched, setSalesEstimateFetched] = useState<boolean>(false);

  const { salesEstimate, fetchSalesEstimate } = React.useContext(EstimateDetailsContext);

  const filesToDownload = [
    {
      url: 'https://tudi-admin-job-folders-production.s3.us-east-2.amazonaws.com/aa44a016-b1ff-4f76-85c2-434504c6c726/Chaska%20Building%20220%20-%20Replace%20RTU-08%203Ton%20.xlsx?response-content-disposition=inline&X-Amz-Security-Token=IQoJb3JpZ2luX2VjED0aCXVzLWVhc3QtMiJHMEUCIQCxx%2BXVUNbNQ%2B%2Beo2TBdLJRDgkaSnkIDqRK%2FdJbZHz7hgIgOUSTqHmV0mAdT%2FoLTA45Vz7oUtWCZLkF5xtC92kXMRsq7AMIpv%2F%2F%2F%2F%2F%2F%2F%2F%2F%2FARAEGgw1MzE5MzEyNDM4OTkiDFOrvr%2BG4XXQBmIsXSrAA4oQeOifMu0%2BqVrAE6JFIM%2Bj6NyVOy8epUP36Ft8NBPjd3Jhljuhh9U358cIMBU1Whokxq06rGEazXnBYykjsWfhT9cIvZKBKX7Ts6MQI7HOZ%2B0whXe6XtLhByCmwBesJGClHL4ZP4228GXpAFh%2B1PiP6mO4QP7qhySr4UcZkrd%2BZmbEoVWNUQhtRJeMkckUEdKFw6iPfHKey8lywZF8JbBArJfY2490dTKh1%2F%2F6w9u%2B3M8MI0tJZmXy5IrKIyS0jE88zrOyA1GQ2bZvDTpRmSbVSxfUkam1wi58FMHmXl2twhaHEJYTIWXP7obOpil5xAHUtXvYLeUelpMnOEJZ8xmt7g49b7Km4DpMidDMgFACnL3oMAtC8xwDQlBzjj8E4ubTqCnJnEH6v028t%2BCpNHIkHCvif2wJT5%2Bwv7h1zT%2Ft34Qth4sGmrD3JDQ2qkYuQUYtNvwMsLheanf5VpFse1JLUIr62cwFkAGYm4o0lxuKRRFrY6LNt3umsR6znDBFq5pD3TQ%2BqatdWuh713ye1dpsG3ZVsF2b1qqPQaAFViTiTMucXh8kUPeaRB%2BGpT%2FYGzpZHJw1z8piAHsAcwIJv%2BgwqOCSsgY6lAI86lVKW1gtDhb0oovsaBP%2BXi510MmRUj9gVb11zoncF%2B0MIEfkvbMYJh2xpg0ZZTw6yMRPOLW%2FGkjRfda69WVcMjgoFpvk9bOaHhAlXf80c0Xl7BZKIys%2BGY%2FJbsPfn%2FteJKfUKbdETK4URFdfyJUM47XW0rTzgjSv9n6iC66JVM%2FMWcgve2soaFV4YlwS2JlvJra463AUi7zv%2BJCnxnOtim1e%2BlMrIXGNo4JQe9g2Z2XbSiRLPesLduvc5gUe6ArWdLr6HW91Q14V4wAhqr8wLqyd5L9Qx1XqGi%2FrF7AwMNbNA6fulgwFb6juHAVQ7Z1KTELtmy%2FV1QIs8fUZUGydI74%2B46d3mcKPmtYwTbwY4l9WVx4%3D&X-Amz-Algorithm=AWS4-HMAC-SHA256&X-Amz-Date=20240515T133243Z&X-Amz-SignedHeaders=host&X-Amz-Expires=43200&X-Amz-Credential=ASIAXXWMSUV5VKVO4MWF%2F20240515%2Fus-east-2%2Fs3%2Faws4_request&X-Amz-Signature=a81b103e125f7b6c4d1e9115d0a418595019349ae152e36ee69b01a840b05263',
      filename: 'Chaska Building 220 - Replace RTU-08.xlsx',
    },
    {
      url: 'https://tudi-admin-job-folders-production.s3.us-east-2.amazonaws.com/aa44a016-b1ff-4f76-85c2-434504c6c726/Tudi%20Chaska%20Tudi%20Chaska%20YSC036%20%2003-19-2024%2003-19-2024.pdf?response-content-disposition=inline&X-Amz-Security-Token=IQoJb3JpZ2luX2VjED0aCXVzLWVhc3QtMiJHMEUCIQCxx%2BXVUNbNQ%2B%2Beo2TBdLJRDgkaSnkIDqRK%2FdJbZHz7hgIgOUSTqHmV0mAdT%2FoLTA45Vz7oUtWCZLkF5xtC92kXMRsq7AMIpv%2F%2F%2F%2F%2F%2F%2F%2F%2F%2FARAEGgw1MzE5MzEyNDM4OTkiDFOrvr%2BG4XXQBmIsXSrAA4oQeOifMu0%2BqVrAE6JFIM%2Bj6NyVOy8epUP36Ft8NBPjd3Jhljuhh9U358cIMBU1Whokxq06rGEazXnBYykjsWfhT9cIvZKBKX7Ts6MQI7HOZ%2B0whXe6XtLhByCmwBesJGClHL4ZP4228GXpAFh%2B1PiP6mO4QP7qhySr4UcZkrd%2BZmbEoVWNUQhtRJeMkckUEdKFw6iPfHKey8lywZF8JbBArJfY2490dTKh1%2F%2F6w9u%2B3M8MI0tJZmXy5IrKIyS0jE88zrOyA1GQ2bZvDTpRmSbVSxfUkam1wi58FMHmXl2twhaHEJYTIWXP7obOpil5xAHUtXvYLeUelpMnOEJZ8xmt7g49b7Km4DpMidDMgFACnL3oMAtC8xwDQlBzjj8E4ubTqCnJnEH6v028t%2BCpNHIkHCvif2wJT5%2Bwv7h1zT%2Ft34Qth4sGmrD3JDQ2qkYuQUYtNvwMsLheanf5VpFse1JLUIr62cwFkAGYm4o0lxuKRRFrY6LNt3umsR6znDBFq5pD3TQ%2BqatdWuh713ye1dpsG3ZVsF2b1qqPQaAFViTiTMucXh8kUPeaRB%2BGpT%2FYGzpZHJw1z8piAHsAcwIJv%2BgwqOCSsgY6lAI86lVKW1gtDhb0oovsaBP%2BXi510MmRUj9gVb11zoncF%2B0MIEfkvbMYJh2xpg0ZZTw6yMRPOLW%2FGkjRfda69WVcMjgoFpvk9bOaHhAlXf80c0Xl7BZKIys%2BGY%2FJbsPfn%2FteJKfUKbdETK4URFdfyJUM47XW0rTzgjSv9n6iC66JVM%2FMWcgve2soaFV4YlwS2JlvJra463AUi7zv%2BJCnxnOtim1e%2BlMrIXGNo4JQe9g2Z2XbSiRLPesLduvc5gUe6ArWdLr6HW91Q14V4wAhqr8wLqyd5L9Qx1XqGi%2FrF7AwMNbNA6fulgwFb6juHAVQ7Z1KTELtmy%2FV1QIs8fUZUGydI74%2B46d3mcKPmtYwTbwY4l9WVx4%3D&X-Amz-Algorithm=AWS4-HMAC-SHA256&X-Amz-Date=20240515T133515Z&X-Amz-SignedHeaders=host&X-Amz-Expires=43200&X-Amz-Credential=ASIAXXWMSUV5VKVO4MWF%2F20240515%2Fus-east-2%2Fs3%2Faws4_request&X-Amz-Signature=a78e5907f68eca4b898f8b4861fab859e5f2467a98149c0f633c711be55aad69',
      filename: 'Tudi Chaska Tudi Chaska YSC036  03-19-2024 03-19-2024.pdf',
    },
    {
      url: 'https://www.trane.com/content/dam/Trane/Commercial/global/products-systems/equipment/unitary/rooftop-systems/precedent-3-to-10-tons/RT-PRC039E-EN_03302020.pdf',
      filename: 'RTU-08 Replacement Manual.pdf',
    },
    {
      url: 'https://tudi-admin-job-folders-production.s3.us-east-2.amazonaws.com/aa44a016-b1ff-4f76-85c2-434504c6c726/YCD180B4%20DisplayAllDrawingsPDF.pdf?response-content-disposition=inline&X-Amz-Security-Token=IQoJb3JpZ2luX2VjED0aCXVzLWVhc3QtMiJHMEUCIQCxx%2BXVUNbNQ%2B%2Beo2TBdLJRDgkaSnkIDqRK%2FdJbZHz7hgIgOUSTqHmV0mAdT%2FoLTA45Vz7oUtWCZLkF5xtC92kXMRsq7AMIpv%2F%2F%2F%2F%2F%2F%2F%2F%2F%2FARAEGgw1MzE5MzEyNDM4OTkiDFOrvr%2BG4XXQBmIsXSrAA4oQeOifMu0%2BqVrAE6JFIM%2Bj6NyVOy8epUP36Ft8NBPjd3Jhljuhh9U358cIMBU1Whokxq06rGEazXnBYykjsWfhT9cIvZKBKX7Ts6MQI7HOZ%2B0whXe6XtLhByCmwBesJGClHL4ZP4228GXpAFh%2B1PiP6mO4QP7qhySr4UcZkrd%2BZmbEoVWNUQhtRJeMkckUEdKFw6iPfHKey8lywZF8JbBArJfY2490dTKh1%2F%2F6w9u%2B3M8MI0tJZmXy5IrKIyS0jE88zrOyA1GQ2bZvDTpRmSbVSxfUkam1wi58FMHmXl2twhaHEJYTIWXP7obOpil5xAHUtXvYLeUelpMnOEJZ8xmt7g49b7Km4DpMidDMgFACnL3oMAtC8xwDQlBzjj8E4ubTqCnJnEH6v028t%2BCpNHIkHCvif2wJT5%2Bwv7h1zT%2Ft34Qth4sGmrD3JDQ2qkYuQUYtNvwMsLheanf5VpFse1JLUIr62cwFkAGYm4o0lxuKRRFrY6LNt3umsR6znDBFq5pD3TQ%2BqatdWuh713ye1dpsG3ZVsF2b1qqPQaAFViTiTMucXh8kUPeaRB%2BGpT%2FYGzpZHJw1z8piAHsAcwIJv%2BgwqOCSsgY6lAI86lVKW1gtDhb0oovsaBP%2BXi510MmRUj9gVb11zoncF%2B0MIEfkvbMYJh2xpg0ZZTw6yMRPOLW%2FGkjRfda69WVcMjgoFpvk9bOaHhAlXf80c0Xl7BZKIys%2BGY%2FJbsPfn%2FteJKfUKbdETK4URFdfyJUM47XW0rTzgjSv9n6iC66JVM%2FMWcgve2soaFV4YlwS2JlvJra463AUi7zv%2BJCnxnOtim1e%2BlMrIXGNo4JQe9g2Z2XbSiRLPesLduvc5gUe6ArWdLr6HW91Q14V4wAhqr8wLqyd5L9Qx1XqGi%2FrF7AwMNbNA6fulgwFb6juHAVQ7Z1KTELtmy%2FV1QIs8fUZUGydI74%2B46d3mcKPmtYwTbwY4l9WVx4%3D&X-Amz-Algorithm=AWS4-HMAC-SHA256&X-Amz-Date=20240515T134410Z&X-Amz-SignedHeaders=host&X-Amz-Expires=43200&X-Amz-Credential=ASIAXXWMSUV5VKVO4MWF%2F20240515%2Fus-east-2%2Fs3%2Faws4_request&X-Amz-Signature=109857020cdfcbc282c46b5453e8e8762f586363f902552f257055cf24d42a2b',
      filename: 'Curb Adapter.pdf',
    },
  ];

  const downloadFile = (url, filename) => {
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const downloadAllFiles = () => {
    filesToDownload.forEach((file) => {
      downloadFile(file.url, file.filename);
    });
  };

  useEffect(() => {
    if (!salesEstimateFetched) {
      fetchSalesEstimate(estimateId);
      setSalesEstimateFetched(true);
    }
  }, [estimateId, salesEstimateFetched]);

  return (
    <Box className={classes.box}>
      <SalesEstimateStepper activeStep={6} salesEstimateId={estimateId} />
      <br />
      <Box>
        <Grid justifyContent="space-between" container spacing={3}>
          <Grid item>
            <EstimateInfoHeader salesEstimate={salesEstimate} />
          </Grid>
          <Grid item>
            <PDFDownloadLink document={<EstimatePDF salesEstimate={salesEstimate} />} fileName="customer_estimate.pdf">
              {({ blob, url, loading, error }) => (
                <Button variant="contained" color="primary" disableElevation>
                  {loading ? 'Loading document...' : 'Proposal'}
                </Button>
              )}
            </PDFDownloadLink>
            <Button
              variant="contained"
              color="primary"
              disableElevation
              onClick={() => history.push(`/rooftop_automation/${estimateId}/spreadsheets`)}
              style={{ marginLeft: 10 }}
            >
              Estimate
            </Button>
            <Button
              variant="contained"
              color="primary"
              disableElevation
              onClick={downloadAllFiles}
              style={{ marginLeft: 10 }}
            >
              Job Folder
            </Button>
          </Grid>
        </Grid>
        <br />
        <Card>
          <CardContent>
            <h3>Estimate Summary</h3>
            <Grid container>
              <Grid item xs={6} md={2}>
                <InformationField label="Sales Price:" value={salesEstimate?.sales_price} />
              </Grid>
              <Grid item xs={6} md={2}>
                <InformationField label="Total Cost:" value={salesEstimate?.total_costs} />
              </Grid>
              <Grid item xs={6} md={2}>
                <InformationField label="Gross Profit:" value={salesEstimate?.gross_profit} />
              </Grid>
              <Grid item xs={6} md={2}>
                <InformationField label="Total Rebates" value={salesEstimate?.total_rebates} />
              </Grid>
              <Grid item xs={6} md={2}>
                <InformationField label="Contact:" value={'David Donley'} />
              </Grid>
              <Grid item xs={6} md={2}>
                <InformationField label="Phone" value={'724-799-3890'} />
              </Grid>
            </Grid>
          </CardContent>
        </Card>
        <br />
        <Card>
          <CardContent>
            <h3>Key Details</h3>
            <DataTable
              title=""
              columns={keyDetailColumns}
              data={[{ id: '' }]}
              striped
              dense
              highlightOnHover
              pointerOnHover
              noHeader
              pagination
            />
          </CardContent>
        </Card>
        <br />
        <Card>
          <CardContent>
            <h3>Margin Details</h3>
            <Grid container spacing={2}>
              <Grid item xs={12} md={3}>
                <Typography gutterBottom>Gross Profit (%)</Typography>
                <TextField
                  type="number"
                  value={grossProfitPercentage}
                  fullWidth
                  sx={{
                    '& input': {
                      padding: '8px',
                    },
                  }}
                  onChange={(e) => setGrossProfitPercentage(Number(e.target.value))}
                  InputProps={{
                    inputProps: {
                      min: 25,
                      max: 36,
                    },
                  }}
                  aria-labelledby="unit-repair-cost-input"
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <Button style={{ marginTop: 31 }} variant="contained" color="primary" onClick={() => null}>
                  Save GP %
                </Button>
              </Grid>
              <Grid item xs={4} md={2}>
                <Typography variant="h6" component="div">
                  Gross Profit:
                </Typography>
                <Typography>{salesEstimate?.gross_profit}</Typography>
              </Grid>
              <Grid item xs={4} md={2}>
                <Typography variant="h6" component="div">
                  Total Hours:
                </Typography>
                <Typography>{salesEstimate?.total_hours}</Typography>
              </Grid>
              <Grid item xs={4} md={2}>
                <Typography variant="h6" component="div">
                  GPPMD:
                </Typography>
                <Typography>{salesEstimate?.gross_profit_per_man_day}</Typography>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
        <br />
        <Card>
          <CardContent>
            <h3>Cost Summary</h3>
            <DataTable
              title=""
              columns={costSummaryColumns}
              data={salesEstimate?.cost_summary}
              striped
              dense
              highlightOnHover
              pointerOnHover
              expandableRows
              expandableRowsComponent={<ExpandedCostBreakdown row />}
              noHeader
            />
          </CardContent>
        </Card>
        <br />
        <Card>
          <CardContent>
            <h3>Asset List</h3>
            <DataTable
              title=""
              columns={assetColumns}
              data={salesEstimate?.sales_estimate_assets}
              striped
              dense
              highlightOnHover
              pointerOnHover
              expandableRows
              expandableRowsComponent={<ExpandedAssetCost row />}
              noHeader
              pagination
            />
          </CardContent>
        </Card>
        <br />
        <Card>
          <CardContent>
            <h3>Rebate Details</h3>
            <DataTable
              title=""
              columns={rebateDetails}
              data={salesEstimate?.rebate_details}
              striped
              dense
              highlightOnHover
              pointerOnHover
              noHeader
              pagination
            />
          </CardContent>
        </Card>
        <br />
        <Card>
          <CardContent>
            <h3>Sales Estimate Change History</h3>
            <DataTable
              title=""
              columns={assetColumns}
              data={[]}
              striped
              dense
              highlightOnHover
              pointerOnHover
              noHeader
              pagination
            />
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
};

export default DecodingDetail;
